import { useEffect, useMemo, useState } from "react";
import {
  ArrowUpDown,
  Building2,
  BusFront,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Eye,
  Plus,
  Printer,
  Route,
  Search,
  Send,
  ShoppingCart,
  Sparkles,
  Trash2,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { useAuth } from "../../contexts/AuthContext";
import { searchVendors, Vendor } from "../../services/vendorMasterService";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import Pagination from "../../ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion";
import {
  loadVendorRequirementBatches,
  saveVendorRequirementBatches,
  subscribeVendorRequirementBatches,
  VehicleRequirement,
  VendorRequirementBatch,
  VendorRequirementLine,
} from "./vendorRequirementStore";

type StatusFilter = "all" | "active" | "inactive";
type DashboardTab = "requests" | "purchase-orders";
type VendorAssignment = {
  tourId: string;
  vehicleId: string;
  date: string;
  time: string;
};
type PurchaseOrderForm = {
  vendor: string;
  batchCode: string;
  poDate: string;
  service: string;
  quantity: string;
  unit: string;
  unitRate: string;
  tax: string;
  delivery: string;
  deliveryLocation: string;
  paymentTerms: string;
  notes: string;
};

const DEMO_TOURS = [
  {
    id: "TOUR-2026-001",
    name: "Najaf Arrival Group A",
    route: "Najaf Airport → Hotel",
    passengers: 42,
  },
  {
    id: "TOUR-2026-002",
    name: "Karbala Ziyarat Group",
    route: "Najaf → Karbala",
    passengers: 28,
  },
  {
    id: "TOUR-2026-003",
    name: "Atraaf Route Movement",
    route: "Kufa → Najaf → Karbala",
    passengers: 18,
  },
  {
    id: "TOUR-2026-004",
    name: "Baghdad Transfer Group",
    route: "Baghdad Airport → Karbala",
    passengers: 35,
  },
  {
    id: "TOUR-2026-005",
    name: "Ladies Ziyarat Group",
    route: "Karbala Local Ziyarat",
    passengers: 24,
  },
];

const DEMO_VEHICLES = [
  { id: "KRB-2048", name: "Volvo Coach", type: "Coach", capacity: 48 },
  { id: "NJF-1187", name: "Yutong Bus", type: "Bus", capacity: 35 },
  { id: "EV-7184", name: "Toyota Hiace", type: "Van", capacity: 14 },
  { id: "EV-6608", name: "Coaster Mini Bus", type: "Mini Bus", capacity: 24 },
  { id: "BGD-3102", name: "Hyundai Staria", type: "Van", capacity: 9 },
];

const EXTERNAL_REQUIREMENTS: VendorRequirementLine[] = [
  {
    movementId: "TM-01",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    movement: "Arrival Transfer",
    pax: 42,
    pickup: "Najaf International Airport",
    pickupDate: "12 Aug 2026",
    pickupTime: "08:45",
    dropoff: "Najaf Faiz",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "09:30",
  },
  {
    movementId: "TM-05",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    movement: "Arrival Transfer",
    pax: 28,
    pickup: "Najaf International Airport",
    pickupDate: "12 Aug 2026",
    pickupTime: "11:20",
    dropoff: "Najaf Faiz",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "12:05",
  },
  {
    movementId: "TM-08",
    tour: "Instant Najaf Test Tour",
    operator: "Najaf Instant Tours",
    movement: "Arrival Transfer",
    pax: 12,
    pickup: "Najaf International Airport",
    pickupDate: "12 Aug 2026",
    pickupTime: "09:30",
    dropoff: "Najaf Faiz",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "10:15",
  },
  {
    movementId: "TM-11",
    tour: "External Hotel Test Tour",
    operator: "External Hotel Operator",
    movement: "Arrival Transfer",
    pax: 31,
    pickup: "Najaf International Airport",
    pickupDate: "12 Aug 2026",
    pickupTime: "10:10",
    dropoff: "Grand Hotel Najaf",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "10:55",
  },
  {
    movementId: "TM-13",
    tour: "Burhani Family Ziyarat",
    operator: "Burhani Holidays",
    movement: "Arrival Transfer",
    pax: 18,
    pickup: "Najaf International Airport",
    pickupDate: "12 Aug 2026",
    pickupTime: "07:30",
    dropoff: "Najaf Faiz",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "08:15",
  },
  {
    movementId: "TM-04",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    movement: "Departure Transfer",
    pax: 42,
    pickup: "Najaf Faiz",
    pickupDate: "12 Aug 2026",
    pickupTime: "22:00",
    dropoff: "Najaf International Airport",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "22:45",
  },
  {
    movementId: "TM-12",
    tour: "External Hotel Test Tour",
    operator: "External Hotel Operator",
    movement: "Departure Transfer",
    pax: 31,
    pickup: "Grand Hotel Najaf",
    pickupDate: "12 Aug 2026",
    pickupTime: "18:10",
    dropoff: "Najaf International Airport",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "18:55",
  },
  {
    movementId: "TM-07",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    movement: "Inter-venue Transfer",
    pax: 28,
    pickup: "Najaf Faiz",
    pickupDate: "12 Aug 2026",
    pickupTime: "21:00",
    dropoff: "Karbala Faiz",
    dropoffDate: "12 Aug 2026",
    dropoffTime: "22:30",
  },
];

const APPLICANT_FIRST_NAMES = [
  "Ali",
  "Hussain",
  "Fatima",
  "Zainab",
  "Abbas",
  "Mustafa",
  "Sakina",
  "Haider",
  "Murtaza",
  "Maryam",
  "Hasan",
  "Batool",
  "Mohammed",
  "Ruqaiya",
  "Jafar",
  "Amina",
];
const APPLICANT_LAST_NAMES = [
  "Al-Rawi",
  "Kareem",
  "Mahdi",
  "Abbas",
  "Salman",
  "Jaffar",
  "Jawad",
  "Qasim",
  "Kazmi",
  "Rizvi",
  "Merchant",
  "Poonawala",
  "Lokhandwala",
  "Shakir",
];

const getTourManifest = (batch: VendorRequirementBatch) =>
  Array.from(new Set(batch.lines.map((line) => line.tour))).map(
    (tour, tourIndex) => {
      const tourLines = batch.lines.filter((line) => line.tour === tour);
      const pax = Math.max(...tourLines.map((line) => line.pax));
      const batchSequence = Number(batch.batchCode.split("-").pop()) || 1;
      return {
        tour,
        operator: tourLines[0]?.operator || "—",
        pax,
        codes: tourLines.map((line) => line.movementId),
        applicants: Array.from({ length: pax }, (_, applicantIndex) => {
          const itsNumber = String(
            10000000 +
              batchSequence * 10000 +
              (tourIndex + 1) * 1000 +
              applicantIndex +
              1,
          );
          const isClubbed = applicantIndex % 5 !== 4;
          const familyStart = applicantIndex - (applicantIndex % 5);
          const hofItsNumber = isClubbed
            ? String(
                10000000 +
                  batchSequence * 10000 +
                  (tourIndex + 1) * 1000 +
                  familyStart +
                  1,
              )
            : itsNumber;
          return {
            name: `${APPLICANT_FIRST_NAMES[(applicantIndex + tourIndex * 3) % APPLICANT_FIRST_NAMES.length]} ${APPLICANT_LAST_NAMES[(applicantIndex * 2 + tourIndex) % APPLICANT_LAST_NAMES.length]}`,
            itsNumber,
            hofItsNumber,
            isClubbed,
            relation: isClubbed
              ? applicantIndex % 5 === 0
                ? "Head of Family"
                : "Family Member"
              : "Individual",
          };
        }),
      };
    },
  );

const DEMO_PURCHASE_ORDERS = [
  {
    po: "PO-TR-26001",
    date: "04 Sep 2026",
    vendor: "Al Safar Transport Services",
    service: "Airport transfer services",
    amount: "USD 3,700.00",
    delivery: "06 Sep 2026",
    status: "Approved",
    batchCode: "VRQ-2026-001",
    quantity: 2,
    unit: "Vehicle",
    unitRate: "USD 1,850.00",
    tax: "USD 0.00",
    deliveryLocation: "Najaf International Airport",
    paymentTerms: "Net 30 Days",
    notes: "Provide vehicles 30 minutes before reporting time.",
  },
  {
    po: "PO-TR-26002",
    date: "04 Sep 2026",
    vendor: "Karbala City Coaches",
    service: "Intercity coach hire",
    amount: "USD 2,450.00",
    delivery: "06 Sep 2026",
    status: "Issued",
    batchCode: "—",
    quantity: 1,
    unit: "Service",
    unitRate: "USD 2,450.00",
    tax: "USD 0.00",
    deliveryLocation: "Najaf Faiz",
    paymentTerms: "Net 30 Days",
    notes: "",
  },
  {
    po: "PO-TR-26003",
    date: "05 Sep 2026",
    vendor: "Zahra Women Transport",
    service: "Dedicated group transport",
    amount: "USD 1,650.00",
    delivery: "07 Sep 2026",
    status: "Pending Approval",
    batchCode: "—",
    quantity: 1,
    unit: "Service",
    unitRate: "USD 1,650.00",
    tax: "USD 0.00",
    deliveryLocation: "Karbala Faiz",
    paymentTerms: "Net 15 Days",
    notes: "",
  },
  {
    po: "PO-TR-26004",
    date: "05 Sep 2026",
    vendor: "Atraaf Route Logistics",
    service: "Atraaf route movement",
    amount: "USD 1,365.00",
    delivery: "07 Sep 2026",
    status: "Issued",
    batchCode: "—",
    quantity: 1,
    unit: "Service",
    unitRate: "USD 1,365.00",
    tax: "USD 0.00",
    deliveryLocation: "Najaf",
    paymentTerms: "Net 30 Days",
    notes: "",
  },
  {
    po: "PO-TR-26005",
    date: "05 Sep 2026",
    vendor: "Iraq Transit Partner",
    service: "Baghdad arrival movement",
    amount: "USD 2,225.00",
    delivery: "08 Sep 2026",
    status: "Draft",
    batchCode: "—",
    quantity: 1,
    unit: "Service",
    unitRate: "USD 2,225.00",
    tax: "USD 0.00",
    deliveryLocation: "Baghdad International Airport",
    paymentTerms: "Net 30 Days",
    notes: "",
  },
];

const DEMO_VENDOR_REQUESTS: Vendor[] = [
  {
    publicId: "vendor-001",
    code: "VND-2026-001",
    name: "Al Safar Transport Services",
    priority: "Primary",
    isActive: true,
    vendorVehicleTypes: [
      { publicId: "vt-coach", name: "Coach" },
      { publicId: "vt-bus", name: "Bus" },
    ],
    venues: [
      { publicId: "venue-najaf", name: "Najaf" },
      { publicId: "venue-karbala", name: "Karbala" },
    ],
    contacts: [
      {
        publicId: "contact-001",
        name: "Ahmed Al-Rawi",
        email: "operations@alsafar.test",
        contactNumber: "+964 770 100 2001",
        department: "Operations",
      },
    ],
  },
  {
    publicId: "vendor-002",
    code: "VND-2026-002",
    name: "Karbala City Coaches",
    priority: "Primary",
    isActive: true,
    vendorVehicleTypes: [{ publicId: "vt-coach", name: "Coach" }],
    venues: [{ publicId: "venue-karbala", name: "Karbala" }],
    contacts: [
      {
        publicId: "contact-002",
        name: "Hussain Kareem",
        email: "dispatch@karbalacoaches.test",
        contactNumber: "+964 780 220 3102",
        department: "Dispatch",
      },
    ],
  },
  {
    publicId: "vendor-003",
    code: "VND-2026-003",
    name: "Najaf Mobility Company",
    priority: "Secondary",
    isActive: false,
    vendorVehicleTypes: [
      { publicId: "vt-van", name: "Van" },
      { publicId: "vt-minibus", name: "Mini Bus" },
    ],
    venues: [{ publicId: "venue-najaf", name: "Najaf" }],
    contacts: [
      {
        publicId: "contact-003",
        name: "Ali Mahdi",
        email: "ali@najafmobility.test",
        contactNumber: "+964 771 330 4203",
        department: "Vendor Relations",
      },
    ],
  },
  {
    publicId: "vendor-004",
    code: "VND-2026-004",
    name: "Baghdad Airport Transfers",
    priority: "Backup",
    isActive: false,
    vendorVehicleTypes: [
      { publicId: "vt-sedan", name: "Sedan" },
      { publicId: "vt-van", name: "Van" },
    ],
    venues: [{ publicId: "venue-baghdad", name: "Baghdad" }],
    contacts: [
      {
        publicId: "contact-004",
        name: "Mustafa Abbas",
        email: "booking@baghdadtransfers.test",
        contactNumber: "+964 790 440 5304",
        department: "Booking",
      },
    ],
  },
  {
    publicId: "vendor-005",
    code: "VND-2026-005",
    name: "Atraaf Route Logistics",
    priority: "Secondary",
    isActive: true,
    vendorVehicleTypes: [
      { publicId: "vt-bus", name: "Bus" },
      { publicId: "vt-minibus", name: "Mini Bus" },
    ],
    venues: [
      { publicId: "venue-najaf", name: "Najaf" },
      { publicId: "venue-kufa", name: "Kufa" },
    ],
    contacts: [
      {
        publicId: "contact-005",
        name: "Murtaza Salman",
        email: "routes@atraaflogistics.test",
        contactNumber: "+964 750 550 6405",
        department: "Route Planning",
      },
    ],
  },
  {
    publicId: "vendor-006",
    code: "VND-2026-006",
    name: "Zahra Women Transport",
    priority: "Primary",
    isActive: true,
    vendorVehicleTypes: [
      { publicId: "vt-van", name: "Van" },
      { publicId: "vt-bus", name: "Bus" },
    ],
    venues: [
      { publicId: "venue-karbala", name: "Karbala" },
      { publicId: "venue-najaf", name: "Najaf" },
    ],
    contacts: [
      {
        publicId: "contact-006",
        name: "Fatima Jaffar",
        email: "support@zahratransport.test",
        contactNumber: "+964 782 660 7506",
        department: "Customer Support",
      },
    ],
  },
  {
    publicId: "vendor-007",
    code: "VND-2026-007",
    name: "Samarra Ziyarat Fleet",
    priority: "Backup",
    isActive: false,
    vendorVehicleTypes: [{ publicId: "vt-coach", name: "Coach" }],
    venues: [{ publicId: "venue-samarra", name: "Samarra" }],
    contacts: [
      {
        publicId: "contact-007",
        name: "Haider Jawad",
        email: "fleet@samarravendor.test",
        contactNumber: "+964 783 770 8607",
        department: "Fleet",
      },
    ],
  },
  {
    publicId: "vendor-008",
    code: "VND-2026-008",
    name: "Iraq Transit Partner",
    priority: "Secondary",
    isActive: true,
    vendorVehicleTypes: [
      { publicId: "vt-coach", name: "Coach" },
      { publicId: "vt-bus", name: "Bus" },
      { publicId: "vt-van", name: "Van" },
    ],
    venues: [
      { publicId: "venue-baghdad", name: "Baghdad" },
      { publicId: "venue-karbala", name: "Karbala" },
    ],
    contacts: [
      {
        publicId: "contact-008",
        name: "Yusuf Qasim",
        email: "control@iraqtransit.test",
        contactNumber: "+964 785 880 9708",
        department: "Control Room",
      },
    ],
  },
];

export function VendorDashboardPage() {
  const { setPageHeader } = usePageHeader();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<DashboardTab>("requests");
  const [selectedPo, setSelectedPo] = useState<
    (typeof DEMO_PURCHASE_ORDERS)[number] | null
  >(null);
  const [purchaseOrders, setPurchaseOrders] = useState([
    ...DEMO_PURCHASE_ORDERS,
  ]);
  const [createPoOpen, setCreatePoOpen] = useState(false);
  const [poForm, setPoForm] = useState<PurchaseOrderForm>({
    vendor: "",
    batchCode: "",
    poDate: "2026-09-07",
    service: "",
    quantity: "1",
    unit: "Vehicle",
    unitRate: "",
    tax: "0",
    delivery: "2026-09-10",
    deliveryLocation: "",
    paymentTerms: "Net 30 Days",
    notes: "",
  });
  const [assignmentVendor, setAssignmentVendor] = useState<Vendor | null>(null);
  const [assignments, setAssignments] = useState<
    Record<string, VendorAssignment>
  >({});
  const [assignmentForm, setAssignmentForm] = useState<VendorAssignment>({
    tourId: "",
    vehicleId: "",
    date: "2026-09-08",
    time: "08:00",
  });
  const [vendorBatches, setVendorBatches] = useState<VendorRequirementBatch[]>(
    [],
  );
  const [selectedBatch, setSelectedBatch] =
    useState<VendorRequirementBatch | null>(null);
  const [selectedPurchaseOrderBatch, setSelectedPurchaseOrderBatch] =
    useState<VendorRequirementBatch | null>(null);
  const [responseBatch, setResponseBatch] =
    useState<VendorRequirementBatch | null>(null);
  const [batchResponse, setBatchResponse] = useState({
    status: "Available",
    note: "",
  });
  const [selectedRequirementIds, setSelectedRequirementIds] = useState<
    string[]
  >([]);
  const [batchCandidateIds, setBatchCandidateIds] = useState<string[]>([]);
  const [isRecommendedBatch, setIsRecommendedBatch] = useState(false);
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [batchVendorId, setBatchVendorId] = useState("");
  const [batchVehicleRequirements, setBatchVehicleRequirements] = useState<
    VehicleRequirement[]
  >([{ type: "Bus", quantity: 1 }]);
  const [batchNotes, setBatchNotes] = useState("");
  const [requirementSearch, setRequirementSearch] = useState("");
  const [requirementSort, setRequirementSort] = useState("movementId");
  const [requirementSortDirection, setRequirementSortDirection] = useState<
    "asc" | "desc"
  >("asc");
  const [batchSort, setBatchSort] = useState("batchCode");
  const [batchSortDirection, setBatchSortDirection] = useState<"asc" | "desc">(
    "desc",
  );
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPageHeader({
      title: "Vendor Dashboard",
      icon: <Truck className="h-4 w-4" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => setPageNo(1), [search, priority, status]);

  useEffect(() => {
    const refresh = () => setVendorBatches(loadVendorRequirementBatches());
    refresh();
    return subscribeVendorRequirementBatches(refresh);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        if (user?.id === "local-demo-admin") {
          const query = search.trim().toLowerCase();
          const filtered = DEMO_VENDOR_REQUESTS.filter(
            (vendor) =>
              (!query ||
                vendor.name.toLowerCase().includes(query) ||
                vendor.code.toLowerCase().includes(query)) &&
              (priority === "all" || vendor.priority === priority),
          );
          const start = (pageNo - 1) * pageSize;
          if (!active) return;
          setVendors(filtered.slice(start, start + pageSize));
          setTotalRecords(filtered.length);
          return;
        }

        const response = await searchVendors({
          Name: search.trim() || undefined,
          Priority: priority === "all" ? undefined : priority,
          PageNumber: pageNo,
          PageSize: pageSize,
        });
        if (!active) return;
        if (!response.success) throw new Error(response.message);
        setVendors(response.data ?? []);
        setTotalRecords(response.totalRecords ?? 0);
      } catch (error) {
        if (!active) return;
        setVendors([]);
        setTotalRecords(0);
        toast.error("Unable to load vendor requests from the server.");
        console.error("Failed to load vendor requests:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [pageNo, pageSize, priority, search, user?.id]);

  const visibleVendors = useMemo(
    () =>
      vendors.filter((vendor) =>
        status === "active"
          ? vendor.isActive
          : status === "inactive"
            ? !vendor.isActive
            : true,
      ),
    [status, vendors],
  );
  const visibleExternalRequirements = useMemo(() => {
    const query = requirementSearch.trim().toLowerCase();
    return EXTERNAL_REQUIREMENTS.filter(
      (line) =>
        !vendorBatches.some((batch) =>
          batch.lines.some(
            (batchLine) => batchLine.movementId === line.movementId,
          ),
        ),
    )
      .filter(
        (line) =>
          !query ||
          [
            line.movementId,
            line.tour,
            line.operator,
            line.movement,
            line.pickup,
            line.dropoff,
          ].some((value) => value.toLowerCase().includes(query)),
      )
      .sort((left, right) => {
        const leftValue =
          requirementSort === "codeTour"
            ? `${left.movementId} ${left.tour}`
            : String(
                left[requirementSort as keyof VendorRequirementLine] ?? "",
              );
        const rightValue =
          requirementSort === "codeTour"
            ? `${right.movementId} ${right.tour}`
            : String(
                right[requirementSort as keyof VendorRequirementLine] ?? "",
              );
        return (
          leftValue.localeCompare(rightValue, undefined, { numeric: true }) *
          (requirementSortDirection === "asc" ? 1 : -1)
        );
      });
  }, [
    requirementSearch,
    requirementSort,
    requirementSortDirection,
    vendorBatches,
  ]);
  const sortedVendorBatches = useMemo(
    () =>
      [...vendorBatches].sort((left, right) => {
        const totalPax = (batch: VendorRequirementBatch) =>
          batch.lines.reduce((sum, line) => sum + line.pax, 0);
        const values: Record<string, [string | number, string | number]> = {
          batchCode: [left.batchCode, right.batchCode],
          vendor: [left.vendorName, right.vendorName],
          codes: [left.lines.length, right.lines.length],
          tours: [
            left.lines.map((line) => line.tour).join(" "),
            right.lines.map((line) => line.tour).join(" "),
          ],
          pax: [totalPax(left), totalPax(right)],
          vehicles: [
            left.vehicleRequirements
              .map((item) => `${item.quantity} ${item.type}`)
              .join(" "),
            right.vehicleRequirements
              .map((item) => `${item.quantity} ${item.type}`)
              .join(" "),
          ],
          routes: [
            left.lines
              .map((line) => `${line.pickup} ${line.dropoff}`)
              .join(" "),
            right.lines
              .map((line) => `${line.pickup} ${line.dropoff}`)
              .join(" "),
          ],
          status: [left.status, right.status],
        };
        const [leftValue, rightValue] = values[batchSort] || values.batchCode;
        const result =
          typeof leftValue === "number" && typeof rightValue === "number"
            ? leftValue - rightValue
            : String(leftValue).localeCompare(String(rightValue), undefined, {
                numeric: true,
              });
        return result * (batchSortDirection === "asc" ? 1 : -1);
      }),
    [vendorBatches, batchSort, batchSortDirection],
  );
  const purchaseOrderBatches = useMemo(() => {
    const unique = new Map<string, VendorRequirementBatch>();
    sortedVendorBatches.forEach((batch) => {
      const existing = unique.get(batch.batchCode);
      if (
        !existing ||
        (existing.status === "Awaiting Response" &&
          batch.status !== "Awaiting Response")
      )
        unique.set(batch.batchCode, batch);
    });
    return Array.from(unique.values());
  }, [sortedVendorBatches]);

  const changeRequirementSort = (key: string) => {
    setRequirementSortDirection((current) =>
      requirementSort === key && current === "asc" ? "desc" : "asc",
    );
    setRequirementSort(key);
  };
  const changeBatchSort = (key: string) => {
    setBatchSortDirection((current) =>
      batchSort === key && current === "asc" ? "desc" : "asc",
    );
    setBatchSort(key);
  };

  const activeCount = vendors.filter((vendor) => vendor.isActive).length;
  const inactiveCount = vendors.filter((vendor) => !vendor.isActive).length;
  const cards = [
    ["Total Requests", totalRecords, Building2, "bg-blue-50 text-blue-700"],
    [
      "Active Vendors",
      activeCount,
      CheckCircle2,
      "bg-emerald-50 text-emerald-700",
    ],
    ["Pending / Inactive", inactiveCount, Clock3, "bg-amber-50 text-amber-700"],
  ] as const;

  const openAssignment = (vendor: Vendor) => {
    setAssignmentVendor(vendor);
    setAssignmentForm(
      assignments[vendor.publicId] ?? {
        tourId: "",
        vehicleId: "",
        date: "2026-09-08",
        time: "08:00",
      },
    );
  };

  const saveAssignment = () => {
    if (
      !assignmentVendor ||
      !assignmentForm.tourId ||
      !assignmentForm.vehicleId ||
      !assignmentForm.date ||
      !assignmentForm.time
    ) {
      toast.error("Select a tour, vehicle, date and time before assigning.");
      return;
    }
    setAssignments((current) => ({
      ...current,
      [assignmentVendor.publicId]: assignmentForm,
    }));
    toast.success(`Tour and vehicle assigned to ${assignmentVendor.name}.`);
    setAssignmentVendor(null);
  };

  const formatPoDate = (value: string) => {
    if (!value) return "—";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${value}T00:00:00Z`));
  };

  const createPurchaseOrder = () => {
    const quantity = Number(poForm.quantity);
    const unitRate = Number(poForm.unitRate);
    const tax = Number(poForm.tax || 0);
    const numericAmount = quantity * unitRate + tax;
    if (
      !poForm.vendor ||
      !poForm.poDate ||
      !poForm.service.trim() ||
      !poForm.delivery ||
      !poForm.deliveryLocation.trim() ||
      !poForm.paymentTerms ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      !Number.isFinite(unitRate) ||
      unitRate <= 0 ||
      !Number.isFinite(tax) ||
      tax < 0
    ) {
      toast.error(
        "Complete all required purchase order fields with valid amounts.",
      );
      return;
    }
    const nextNumber = 26001 + purchaseOrders.length;
    const purchaseOrder = {
      po: `PO-TR-${nextNumber}`,
      date: formatPoDate(poForm.poDate),
      vendor: poForm.vendor,
      service: poForm.service.trim(),
      amount: `USD ${numericAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      delivery: formatPoDate(poForm.delivery),
      status: "Draft",
      batchCode: poForm.batchCode || "—",
      quantity,
      unit: poForm.unit,
      unitRate: `USD ${unitRate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      tax: `USD ${tax.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      deliveryLocation: poForm.deliveryLocation.trim(),
      paymentTerms: poForm.paymentTerms,
      notes: poForm.notes.trim(),
    };
    setPurchaseOrders((current) => [purchaseOrder, ...current]);
    setCreatePoOpen(false);
    setSelectedPo(purchaseOrder);
    toast.success(`${purchaseOrder.po} created successfully.`);
  };

  const openBatchResponse = (batch: VendorRequirementBatch) => {
    setResponseBatch(batch);
    setBatchResponse({
      status: batch.status === "Awaiting Response" ? "Available" : batch.status,
      note: batch.vendorResponse || "",
    });
  };

  const saveBatchResponse = () => {
    if (!responseBatch) return;
    const updated = vendorBatches.map((batch) =>
      batch.id === responseBatch.id
        ? {
            ...batch,
            status: batchResponse.status as VendorRequirementBatch["status"],
            vendorResponse: batchResponse.note.trim(),
          }
        : batch,
    );
    setVendorBatches(updated);
    saveVendorRequirementBatches(updated);
    setResponseBatch(null);
    toast.success(
      `Availability response recorded for ${responseBatch.batchCode}.`,
    );
  };

  const openCreateBatch = () => {
    if (!selectedRequirementIds.length)
      return toast.error("Select one or more transport requirements first.");
    setBatchCandidateIds(selectedRequirementIds);
    setIsRecommendedBatch(false);
    setBatchVendorId("");
    setBatchVehicleRequirements([{ type: "Bus", quantity: 1 }]);
    setBatchNotes("");
    setCreateBatchOpen(true);
  };

  const createRequirementBatch = () => {
    const selectedLines = EXTERNAL_REQUIREMENTS.filter((line) =>
      selectedRequirementIds.includes(line.movementId),
    );
    const vendor = DEMO_VENDOR_REQUESTS.find(
      (item) => item.publicId === batchVendorId,
    );
    if (
      !selectedLines.length ||
      !vendor ||
      batchVehicleRequirements.some((item) => !item.type || item.quantity < 1)
    ) {
      toast.error("Select a vendor and enter valid vehicle requirements.");
      return;
    }
    const nextSequence =
      vendorBatches.reduce(
        (highest, batch) =>
          Math.max(highest, Number(batch.batchCode.split("-").pop()) || 0),
        0,
      ) + 1;
    const batch: VendorRequirementBatch = {
      id: `vendor-batch-${Date.now()}`,
      batchCode: `VRQ-2026-${String(nextSequence).padStart(3, "0")}`,
      vendorId: vendor.publicId,
      vendorName: vendor.name,
      createdAt: "07 Sep 2026",
      status: "Awaiting Response",
      lines: selectedLines,
      vehicleRequirements: batchVehicleRequirements,
      notes: batchNotes.trim(),
    };
    const updated = [batch, ...vendorBatches];
    setVendorBatches(updated);
    saveVendorRequirementBatches(updated);
    setSelectedRequirementIds([]);
    setCreateBatchOpen(false);
    setActiveTab("purchase-orders");
    setSelectedPurchaseOrderBatch(batch);
    toast.success(`${batch.batchCode} created and sent to ${vendor.name}.`);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-0 border-b border-border">
        {(
          [
            ["requests", "Vendor Requests", Building2],
            ["purchase-orders", "Purchase Orders", ShoppingCart],
          ] as const
        ).map(([value, label, Icon]) => (
          <button
            key={value}
            type="button"
            onClick={() => setActiveTab(value)}
            className={`relative flex items-center gap-2 px-5 py-3 text-sm hover:text-foreground ${activeTab === value ? "text-primary" : "text-muted-foreground"}`}
          >
            <Icon className="h-4 w-4" />
            <span className="font-medium">{label}</span>
            {activeTab === value && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        ))}
      </div>

      {activeTab === "requests" && (
        <>
          <Card
            className="h-full overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm"
            style={{ gap: "0px" }}
          >
            <CardContent
              className="flex h-full flex-col"
              style={{ padding: "0px 0px 10px 0px" }}
            >
              <div className="grid items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5 lg:grid-cols-[minmax(300px,1.2fr)_minmax(220px,1fr)_auto]">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Truck className="h-4 w-4 text-sky-700" />
                    External Transport Requirements
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Select transfer lines across tours and create one vendor
                    batch
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={requirementSearch}
                    onChange={(event) =>
                      setRequirementSearch(event.target.value)
                    }
                    placeholder="Search tour, operator, route or airport…"
                    className="h-8 bg-white pl-7 text-[9px]"
                  />
                </div>
                <div className="flex gap-2">
                  {/* <Button
                    variant="outline"
                    onClick={recommendBatch}
                    className="h-8 gap-1.5 border-amber-300 bg-amber-50 px-3 text-[9px] text-amber-800 hover:bg-amber-100"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Recommend Batch
                  </Button> */}
                  <Button
                    disabled={!selectedRequirementIds.length}
                    onClick={openCreateBatch}
                    className="h-8 min-w-[160px] gap-1.5 bg-sky-600 px-3 text-[9px] text-white hover:bg-sky-700"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Create Batch{" "}
                    {selectedRequirementIds.length
                      ? `(${selectedRequirementIds.length})`
                      : ""}
                  </Button>
                </div>
              </div>
              {selectedRequirementIds.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                  <span>
                    <b>{selectedRequirementIds.length}</b> codes selected
                  </span>
                  <span>
                    <b>
                      {EXTERNAL_REQUIREMENTS.filter((line) =>
                        selectedRequirementIds.includes(line.movementId),
                      ).reduce((sum, line) => sum + line.pax, 0)}
                    </b>{" "}
                    passenger movements
                  </span>
                  <span>
                    <b>
                      {
                        new Set(
                          EXTERNAL_REQUIREMENTS.filter((line) =>
                            selectedRequirementIds.includes(line.movementId),
                          ).map((line) => `${line.pickup}-${line.dropoff}`),
                        ).size
                      }
                    </b>{" "}
                    routes
                  </span>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                      <TableHead className="w-10 px-4 py-3 text-xs text-[#5B9BD5]">
                        <input
                          type="checkbox"
                          aria-label="Select all requirements"
                          checked={
                            visibleExternalRequirements.length > 0 &&
                            visibleExternalRequirements.every((line) =>
                              selectedRequirementIds.includes(line.movementId),
                            )
                          }
                          onChange={(event) =>
                            setSelectedRequirementIds(
                              event.target.checked
                                ? Array.from(
                                    new Set([
                                      ...selectedRequirementIds,
                                      ...visibleExternalRequirements.map(
                                        (line) => line.movementId,
                                      ),
                                    ]),
                                  )
                                : selectedRequirementIds.filter(
                                    (id) =>
                                      !visibleExternalRequirements.some(
                                        (line) => line.movementId === id,
                                      ),
                                  ),
                            )
                          }
                        />
                      </TableHead>
                      {[
                        ["Code / Tour", "tour"],
                        ["Movement", "movement"],
                        ["Pax", "pax"],
                        ["Pickup Location & Time", "pickup"],
                        ["Drop-off Location & Time", "dropoff"],
                      ].map(([label, key]) => (
                        <TableHead
                          key={key}
                          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#5B9BD5]"
                        >
                          <button
                            type="button"
                            onClick={() => changeRequirementSort(key)}
                            className="flex items-center gap-1 whitespace-nowrap text-[#5B9BD5]"
                          >
                            {label}
                            <ArrowUpDown
                              className={`h-3 w-3 ${requirementSort === key ? "text-sky-700" : "text-sky-400"}`}
                            />
                          </button>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleExternalRequirements.map((line) => (
                      <TableRow
                        key={line.movementId}
                        className={`border-sky-100 text-[9px] ${selectedRequirementIds.includes(line.movementId) ? "bg-sky-50" : "hover:bg-sky-50/40"}`}
                      >
                        <TableCell className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selectedRequirementIds.includes(
                              line.movementId,
                            )}
                            onChange={() =>
                              setSelectedRequirementIds((current) =>
                                current.includes(line.movementId)
                                  ? current.filter(
                                      (id) => id !== line.movementId,
                                    )
                                  : [...current, line.movementId],
                              )
                            }
                          />
                        </TableCell>
                        <TableCell className="max-w-[260px] px-4 py-3">
                          <div className="font-semibold text-sky-700">
                            {line.movementId}
                          </div>
                          <div className="text-sm font-medium text-foreground">
                            {line.tour}
                          </div>
                          <div className="pt-1 text-xs text-muted-foreground">
                            {line.operator}
                          </div>
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <Badge
                            variant="outline"
                            className={`justify-center px-1 py-0 text-[7px] ${line.movement === "Arrival Transfer" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : line.movement === "Departure Transfer" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-sky-200 bg-sky-50 text-sky-700"}`}
                          >
                            {line.movement}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-xs font-semibold text-slate-700">
                          {line.pax}
                        </TableCell>
                        <TableCell className="min-w-[210px] px-4 py-3">
                          <div className="text-xs font-medium text-slate-700">
                            {line.pickup}
                          </div>
                          <div className="pt-1 text-[10px] text-muted-foreground">
                            {line.pickupDate} · {line.pickupTime}
                          </div>
                        </TableCell>
                        <TableCell className="min-w-[210px] px-4 py-3">
                          <div className="text-xs font-medium text-slate-700">
                            {line.dropoff}
                          </div>
                          <div className="pt-1 text-[10px] text-muted-foreground">
                            {line.dropoffDate} · {line.dropoffTime}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {false && (
            <>
              <Card
                className="h-full overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm"
                style={{ gap: "0px" }}
              >
                <CardContent
                  className="flex h-full flex-col"
                  style={{ padding: "0px 0px 10px 0px" }}
                >
                  <div className="flex flex-col justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5 sm:flex-row sm:items-center">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <ClipboardList className="h-4 w-4 text-sky-700" />
                        Created Vendor Batches
                      </div>
                      <div className="text-[11px] text-slate-500">
                        Consolidated requirements created from selected
                        transport codes and sent to vendors
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className="w-fit border-sky-200 bg-white px-2 py-0.5 text-[9px] text-sky-700"
                    >
                      {vendorBatches.length} batch request
                      {vendorBatches.length === 1 ? "" : "s"}
                    </Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                          {[
                            ["Batch / Created", "batchCode"],
                            ["Vendor", "vendor"],
                            ["Codes", "codes"],
                            ["Total Pax", "pax"],
                            ["Vehicle Requirement", "vehicles"],
                            ["Route Summary", "routes"],
                            ["Status", "status"],
                          ].map(([label, key]) => (
                            <TableHead
                              key={key}
                              className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#5B9BD5]"
                            >
                              <button
                                type="button"
                                onClick={() => changeBatchSort(key)}
                                className="flex items-center gap-1 whitespace-nowrap text-[#5B9BD5]"
                              >
                                {label}
                                <ArrowUpDown
                                  className={`h-3 w-3 ${batchSort === key ? "text-sky-700" : "text-sky-400"}`}
                                />
                              </button>
                            </TableHead>
                          ))}
                          <TableHead className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[#5B9BD5]">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vendorBatches.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="h-28 text-center text-xs text-slate-500"
                            >
                              <div className="font-medium">
                                No external vendor batches yet.
                              </div>
                              <div className="mt-1 text-[10px]">
                                Select requirements above and choose “Create
                                Batch”.
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedVendorBatches.map((batch) => {
                            const totalPax = batch.lines.reduce(
                              (sum, line) => sum + line.pax,
                              0,
                            );
                            const uniqueRoutes = Array.from(
                              new Set(
                                batch.lines.map(
                                  (line) => `${line.pickup} → ${line.dropoff}`,
                                ),
                              ),
                            );
                            return (
                              <TableRow
                                key={batch.id}
                                className="border-sky-100 text-[9px] hover:bg-sky-50/40"
                              >
                                <TableCell className="px-3 py-2">
                                  <div className="text-xs font-semibold text-sky-700">
                                    {batch.batchCode}
                                  </div>
                                  <div className="text-[10px] text-slate-500">
                                    {batch.createdAt}
                                  </div>
                                </TableCell>
                                <TableCell className="px-3 py-2 text-xs font-medium text-slate-800">
                                  {batch.vendorName}
                                </TableCell>
                                <TableCell className="px-3 py-2 text-xs">
                                  {batch.lines.length}
                                  <div className="text-[10px] text-slate-500">
                                    {batch.lines
                                      .map((line) => line.movementId)
                                      .join(", ")}
                                  </div>
                                </TableCell>
                                <TableCell className="px-3 py-2 text-xs font-semibold">
                                  {totalPax}
                                </TableCell>
                                <TableCell className="px-3 py-2 text-xs">
                                  {batch.vehicleRequirements
                                    .map(
                                      (item) =>
                                        `${item.quantity} × ${item.type}`,
                                    )
                                    .join(", ")}
                                </TableCell>
                                <TableCell className="min-w-[260px] px-3 py-2">
                                  <div className="space-y-1 text-[10px]">
                                    {uniqueRoutes.map((route) => (
                                      <div
                                        key={route}
                                        className="flex items-start gap-1.5"
                                      >
                                        <Route className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" />
                                        <span>{route}</span>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell className="px-3 py-2">
                                  <Badge
                                    className={`px-1 py-0 text-[7px] ${batch.status === "Available" ? "bg-emerald-100 text-emerald-700" : batch.status === "Unavailable" ? "bg-rose-100 text-rose-700" : batch.status === "Partially Available" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}
                                  >
                                    {batch.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-3 py-2">
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-[7px]"
                                      onClick={() => setSelectedBatch(batch)}
                                    >
                                      <Eye className="mr-1 h-3 w-3" />
                                      View
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-6 bg-sky-600 px-2 text-[7px] text-white hover:bg-sky-700"
                                      onClick={() => openBatchResponse(batch)}
                                    >
                                      {batch.status === "Awaiting Response"
                                        ? "Respond"
                                        : "Update"}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {cards.map(([label, value, Icon, color]) => (
                  <Card key={label} className="border-slate-200 shadow-sm">
                    <CardContent className="flex items-center justify-between p-5">
                      <div>
                        <p className="text-sm text-slate-500">{label}</p>
                        <p className="mt-1 text-2xl font-semibold">{value}</p>
                      </div>
                      <div className={`rounded-xl p-3 ${color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="mb-4">
                    <h2 className="text-base font-semibold text-slate-900">
                      Vendor Directory &amp; Final Assignment
                    </h2>
                    <p className="text-xs text-slate-500">
                      Use this section after availability is confirmed to record
                      the final tour and vehicle assignment.
                    </p>
                  </div>
                  <div className="mb-5 flex flex-col gap-3 lg:flex-row">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search vendor requests"
                        className="pl-9"
                      />
                    </div>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger className="lg:w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All priorities</SelectItem>
                        <SelectItem value="Primary">Primary</SelectItem>
                        <SelectItem value="Secondary">Secondary</SelectItem>
                        <SelectItem value="Backup">Backup</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={status}
                      onValueChange={(value) =>
                        setStatus(value as StatusFilter)
                      }
                    >
                      <SelectTrigger className="lg:w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">
                          Pending / Inactive
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="overflow-hidden rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Request / Code</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Priority</TableHead>
                          <TableHead>Vehicle Types</TableHead>
                          <TableHead>Venues</TableHead>
                          <TableHead>Primary Contact</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="min-w-[190px]">
                            Assignment
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={8} className="h-32 text-center">
                              Loading vendor requests…
                            </TableCell>
                          </TableRow>
                        ) : visibleVendors.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="h-32 text-center text-slate-500"
                            >
                              No vendor requests found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          visibleVendors.map((vendor) => {
                            const contact = vendor.contacts?.[0];
                            const assignment = assignments[vendor.publicId];
                            const assignedTour = DEMO_TOURS.find(
                              (tour) => tour.id === assignment?.tourId,
                            );
                            const assignedVehicle = DEMO_VEHICLES.find(
                              (vehicle) => vehicle.id === assignment?.vehicleId,
                            );
                            return (
                              <TableRow key={vendor.publicId}>
                                <TableCell>
                                  {vendor.code || vendor.publicId}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {vendor.name}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {vendor.priority || "—"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {vendor.vendorVehicleTypes
                                    ?.map((x) => x.name)
                                    .join(", ") || "—"}
                                </TableCell>
                                <TableCell>
                                  {vendor.venues
                                    ?.map((x) => x.name)
                                    .join(", ") || "—"}
                                </TableCell>
                                <TableCell>
                                  {contact?.name || "—"}
                                  {contact?.contactNumber && (
                                    <div className="text-xs text-slate-500">
                                      {contact.contactNumber}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    className={
                                      vendor.isActive
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-amber-100 text-amber-700"
                                    }
                                  >
                                    {vendor.isActive
                                      ? "Active"
                                      : "Pending / Inactive"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {assignment ? (
                                    <div className="space-y-2">
                                      <div className="text-xs">
                                        <div className="font-medium text-slate-800">
                                          {assignedTour?.id}
                                        </div>
                                        <div className="text-slate-500">
                                          {assignedVehicle?.id} ·{" "}
                                          {assignment.date} {assignment.time}
                                        </div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs"
                                        onClick={() => openAssignment(vendor)}
                                      >
                                        Reassign
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      size="sm"
                                      className="gap-1.5 bg-[#5B9BD5] text-white hover:bg-[#4A8AC5]"
                                      onClick={() => openAssignment(vendor)}
                                    >
                                      <Route className="h-4 w-4" />
                                      Assign
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4">
                    <Pagination
                      currentPage={pageNo}
                      totalItems={totalRecords}
                      itemsPerPage={pageSize}
                      onPageChange={setPageNo}
                      onPageSizeChange={setPageSize}
                      label="vendor requests"
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {activeTab === "purchase-orders" && (
        <Card
          className="h-full overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm"
          style={{ gap: "0px" }}
        >
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <div className="border-b border-slate-200 bg-slate-50 px-3 py-2.5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ShoppingCart className="h-4 w-4 text-sky-700" />
                Final Vendor Purchase Orders
              </div>
              <div className="text-[11px] text-slate-500">
                Every created vendor batch is converted automatically into a
                final transport request—without service charges or amounts.
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    {[
                      ["PO / Batch", "batchCode"],
                      ["Vendor", "vendor"],
                      ["Tours & Pax", "tours"],
                      ["Vehicle Requirement", "vehicles"],
                      ["Route Requirement", "routes"],
                    ].map(([label, key]) => (
                      <TableHead
                        key={key}
                        className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#5B9BD5]"
                      >
                        <button
                          type="button"
                          onClick={() => changeBatchSort(key)}
                          className="flex items-center gap-1 whitespace-nowrap text-[#5B9BD5]"
                        >
                          {label}
                          <ArrowUpDown
                            className={`h-3 w-3 ${batchSort === key ? "text-sky-700" : "text-sky-400"}`}
                          />
                        </button>
                      </TableHead>
                    ))}
                    <TableHead className="px-3 py-2 text-right text-xs font-semibold tracking-wide text-[#5B9BD5]">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchaseOrderBatches.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-32 text-center text-xs text-slate-500"
                      >
                        <div className="font-medium">
                          No final purchase orders available.
                        </div>
                        <div className="mt-1 text-[10px]">
                          Create a vendor requirement batch to generate its
                          purchase order.
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseOrderBatches.map((batch) => {
                      const tourSummary = getTourManifest(batch);
                      const routeSummary = Array.from(
                        new Set(
                          batch.lines.map(
                            (line) => `${line.pickup} → ${line.dropoff}`,
                          ),
                        ),
                      );
                      return (
                        <TableRow
                          key={batch.batchCode}
                          className="cursor-pointer border-sky-100 text-[9px] hover:bg-sky-50/40"
                          onClick={() => setSelectedPurchaseOrderBatch(batch)}
                        >
                          <TableCell className="px-3 py-2">
                            <div className="text-xs font-semibold text-sky-700">
                              PO-{batch.batchCode.replace("VRQ-", "")}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {batch.batchCode} · {batch.createdAt}
                            </div>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-xs font-medium">
                            {batch.vendorName}
                          </TableCell>
                          <TableCell className="min-w-[230px] px-3 py-2">
                            <div className="space-y-1">
                              {tourSummary.map((item) => (
                                <div key={item.tour}>
                                  <span className="text-xs font-medium text-slate-800">
                                    {item.tour}
                                  </span>
                                  <span className="ml-2 text-[10px] font-semibold text-sky-700">
                                    {item.pax} pax
                                  </span>
                                  <div className="text-[9px] text-slate-500">
                                    {item.codes.join(", ")}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-xs">
                            {batch.vehicleRequirements
                              .map((item) => `${item.quantity} × ${item.type}`)
                              .join(", ")}
                          </TableCell>
                          <TableCell className="min-w-[240px] px-3 py-2">
                            <div className="space-y-1 text-[10px]">
                              {routeSummary.map((item) => (
                                <div key={item} className="flex gap-1">
                                  <Route className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" />
                                  {item}
                                </div>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-right">
                            <Button
                              size="icon"
                              variant="ghost"
                              title="View purchase order"
                              className="h-7 w-7 text-sky-600 hover:bg-sky-100"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedPurchaseOrderBatch(batch);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!selectedPurchaseOrderBatch}
        onOpenChange={(open) => !open && setSelectedPurchaseOrderBatch(null)}
      >
        <DialogContent className="!w-[94vw] !max-w-[1200px] max-h-[92vh] overflow-y-auto bg-white p-4">
          <DialogHeader className="border-b pb-3">
            <DialogTitle>Final Transport Purchase Order</DialogTitle>
          </DialogHeader>
          {selectedPurchaseOrderBatch &&
            (() => {
              const batch = selectedPurchaseOrderBatch;
              const tourSummary = getTourManifest(batch);
              const totalPax = tourSummary.reduce(
                (sum, item) => sum + item.pax,
                0,
              );
              return (
                <div
                  id="vendor-final-purchase-order"
                  className="space-y-3 text-sm text-slate-700"
                >
                  <div className="flex flex-col justify-between gap-3 border-b border-slate-200 pb-3 sm:flex-row">
                    <div>
                      <div className="text-xl font-bold tracking-wide text-[#0B5394]">
                        FHIMS
                      </div>
                      <div className="text-sm font-medium">
                        Faiz e Husaini – Iraq · Transport Operations
                      </div>
                    </div>
                    <div className="sm:text-right">
                      <div className="text-lg font-bold text-slate-800">
                        TRANSPORT PURCHASE ORDER
                      </div>
                      <div className="font-semibold text-[#0B5394]">
                        PO-{batch.batchCode.replace("VRQ-", "")}
                      </div>
                      <div className="text-xs text-slate-500">
                        {batch.batchCode} · {batch.createdAt}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="rounded-lg border px-3 py-2">
                      <span className="text-[10px] font-bold uppercase text-slate-400">
                        Issued To
                      </span>
                      <div className="font-semibold text-slate-900">
                        {batch.vendorName}
                      </div>
                    </div>
                    <div className="rounded-lg border px-3 py-2">
                      <span className="text-[10px] font-bold uppercase text-slate-400">
                        Issued By
                      </span>
                      <div className="font-semibold text-slate-900">
                        Faiz e Husaini – Iraq
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs sm:grid-cols-3">
                    <div>
                      <span className="text-slate-500">Tours: </span>
                      <b>{tourSummary.length}</b>
                    </div>
                    <div>
                      <span className="text-slate-500">Transport Codes: </span>
                      <b>{batch.lines.length}</b>
                    </div>
                    <div>
                      <span className="text-slate-500">Total Applicants: </span>
                      <b>{totalPax}</b>
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-slate-900">
                      Tour-wise Passenger Summary
                    </h3>
                    <div className="overflow-x-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-100">
                            <TableHead>#</TableHead>
                            <TableHead>Tour</TableHead>
                            <TableHead>Tour Operator</TableHead>
                            <TableHead>Transport Codes</TableHead>
                            <TableHead className="text-right">Pax</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tourSummary.map((item, index) => (
                            <TableRow key={item.tour}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell className="font-medium">
                                {item.tour}
                              </TableCell>
                              <TableCell>{item.operator}</TableCell>
                              <TableCell>{item.codes.join(", ")}</TableCell>
                              <TableCell className="text-right font-semibold">
                                {item.pax}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-1.5 text-sm font-semibold text-slate-900">
                      Tour-wise Applicant Manifest
                    </h3>
                    <Accordion type="multiple" className="space-y-2">
                      {tourSummary.map((tourItem) => (
                        <AccordionItem
                          key={tourItem.tour}
                          value={tourItem.tour}
                          className="overflow-hidden rounded-lg border border-slate-200"
                        >
                          <AccordionTrigger className="bg-sky-50 px-3 py-2 hover:no-underline">
                            <div className="flex min-w-0 flex-1 items-center justify-between gap-3 pr-2 text-left">
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-slate-900">
                                  {tourItem.tour}
                                </div>
                                <div className="truncate text-[10px] font-normal text-slate-500">
                                  {tourItem.operator} ·{" "}
                                  {tourItem.codes.join(", ")}
                                </div>
                              </div>
                              <Badge
                                variant="outline"
                                className="shrink-0 border-sky-200 bg-white text-[10px] text-sky-700"
                              >
                                {tourItem.pax} applicants
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="p-2">
                            {(() => {
                              const clubbed = tourItem.applicants.filter(
                                (applicant) => applicant.isClubbed,
                              );
                              const familyIds = Array.from(
                                new Set(
                                  clubbed.map(
                                    (applicant) => applicant.hofItsNumber,
                                  ),
                                ),
                              );
                              const families = familyIds.map((hofItsNumber) => {
                                const members = clubbed.filter(
                                  (applicant) =>
                                    applicant.hofItsNumber === hofItsNumber,
                                );
                                const head =
                                  members.find(
                                    (applicant) =>
                                      applicant.itsNumber === hofItsNumber,
                                  ) || members[0];
                                return { hofItsNumber, head, members };
                              });
                              const individuals = tourItem.applicants.filter(
                                (applicant) => !applicant.isClubbed,
                              );
                              return (
                                <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                                  {families.length > 0 && (
                                    <section>
                                      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-violet-700">
                                        Clubbed HOF Families
                                      </div>
                                      <div className="space-y-2">
                                        {families.map((family) => (
                                          <div
                                            key={family.hofItsNumber}
                                            className="overflow-hidden rounded-md border border-violet-200"
                                          >
                                            <div className="flex items-center justify-between gap-3 bg-violet-50 px-3 py-1.5">
                                              <div className="min-w-0">
                                                <span className="text-xs font-semibold text-slate-900">
                                                  Family Head:{" "}
                                                  {family.head.name}
                                                </span>
                                                <span className="ml-2 text-[10px] font-mono text-violet-700">
                                                  HOF ITS: {family.hofItsNumber}
                                                </span>
                                              </div>
                                              <Badge
                                                variant="outline"
                                                className="shrink-0 border-violet-200 bg-white px-1.5 py-0 text-[9px] text-violet-700"
                                              >
                                                {family.members.length} members
                                              </Badge>
                                            </div>
                                            <Table>
                                              <TableHeader>
                                                <TableRow className="bg-slate-50">
                                                  <TableHead className="w-12 py-1">
                                                    #
                                                  </TableHead>
                                                  <TableHead className="py-1">
                                                    Applicant Name
                                                  </TableHead>
                                                  <TableHead className="py-1">
                                                    ITS Number
                                                  </TableHead>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {family.members.map(
                                                  (applicant, index) => (
                                                    <TableRow
                                                      key={applicant.itsNumber}
                                                      className="text-xs"
                                                    >
                                                      <TableCell className="py-1">
                                                        {index + 1}
                                                      </TableCell>
                                                      <TableCell className="py-1 font-medium">
                                                        {applicant.name}
                                                      </TableCell>
                                                      <TableCell className="py-1 font-mono text-sky-700">
                                                        {applicant.itsNumber}
                                                      </TableCell>
                                                    </TableRow>
                                                  ),
                                                )}
                                              </TableBody>
                                            </Table>
                                          </div>
                                        ))}
                                      </div>
                                    </section>
                                  )}
                                  {individuals.length > 0 && (
                                    <section>
                                      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                                        Non-clubbed Applicants
                                      </div>
                                      <div className="overflow-hidden rounded-md border border-slate-200">
                                        <Table>
                                          <TableHeader>
                                            <TableRow className="bg-slate-50">
                                              <TableHead className="w-12 py-1">
                                                #
                                              </TableHead>
                                              <TableHead className="py-1">
                                                Applicant Name
                                              </TableHead>
                                              <TableHead className="py-1">
                                                ITS Number
                                              </TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {individuals.map(
                                              (applicant, index) => (
                                                <TableRow
                                                  key={applicant.itsNumber}
                                                  className="text-xs"
                                                >
                                                  <TableCell className="py-1">
                                                    {index + 1}
                                                  </TableCell>
                                                  <TableCell className="py-1 font-medium">
                                                    {applicant.name}
                                                  </TableCell>
                                                  <TableCell className="py-1 font-mono text-sky-700">
                                                    {applicant.itsNumber}
                                                  </TableCell>
                                                </TableRow>
                                              ),
                                            )}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    </section>
                                  )}
                                </div>
                              );
                            })()}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-slate-900">
                      Required Vehicles
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {batch.vehicleRequirements.map((item) => (
                        <Badge
                          key={item.type}
                          variant="outline"
                          className="border-sky-200 bg-sky-50 px-3 py-1.5 text-sky-800"
                        >
                          {item.quantity} × {item.type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-slate-900">
                      Route and Movement Schedule
                    </h3>
                    <div className="overflow-x-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-100">
                            <TableHead>Code</TableHead>
                            <TableHead>Tour</TableHead>
                            <TableHead>Movement</TableHead>
                            <TableHead>Pax</TableHead>
                            <TableHead>Pickup</TableHead>
                            <TableHead>Drop-off</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {batch.lines.map((line) => (
                            <TableRow key={line.movementId}>
                              <TableCell className="font-semibold text-sky-700">
                                {line.movementId}
                              </TableCell>
                              <TableCell>{line.tour}</TableCell>
                              <TableCell>{line.movement}</TableCell>
                              <TableCell>{line.pax}</TableCell>
                              <TableCell>
                                <b className="font-medium">{line.pickup}</b>
                                <div className="text-xs text-slate-500">
                                  {line.pickupDate} · {line.pickupTime}
                                </div>
                              </TableCell>
                              <TableCell>
                                <b className="font-medium">{line.dropoff}</b>
                                <div className="text-xs text-slate-500">
                                  {line.dropoffDate} · {line.dropoffTime}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  {batch.notes && (
                    <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
                      <div className="text-[10px] font-bold uppercase text-sky-700">
                        Instructions / Remarks
                      </div>
                      <div className="mt-0.5 whitespace-pre-wrap text-xs">
                        {batch.notes}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 border-t pt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedPurchaseOrderBatch(null)}
                    >
                      Close
                    </Button>
                    <Button
                      size="sm"
                      className="gap-2 bg-[#5B9BD5] text-white hover:bg-[#4A8AC5]"
                      onClick={() => window.print()}
                    >
                      <Printer className="h-4 w-4" />
                      Print
                    </Button>
                  </div>
                </div>
              );
            })()}
        </DialogContent>
      </Dialog>

      <Dialog open={createBatchOpen} onOpenChange={setCreateBatchOpen}>
        <DialogContent className="!w-[88vw] !max-w-[980px] max-h-[86vh] overflow-y-auto bg-white p-4">
          <DialogHeader className="border-b pb-3">
            <DialogTitle className="flex items-center gap-2 text-lg">
              {isRecommendedBatch ? (
                <Sparkles className="h-4 w-4 text-amber-500" />
              ) : (
                <ClipboardList className="h-4 w-4 text-indigo-600" />
              )}
              {isRecommendedBatch
                ? "Recommended Vendor Batch"
                : "Create Vendor Requirement Batch"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {isRecommendedBatch && (
              <div className="grid gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 lg:grid-cols-2">
                <div>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-800">
                    Recommended routes
                  </div>
                  <div className="space-y-1.5">
                    {Array.from(
                      new Set(
                        EXTERNAL_REQUIREMENTS.filter((line) =>
                          selectedRequirementIds.includes(line.movementId),
                        ).map((line) => `${line.pickup} → ${line.dropoff}`),
                      ),
                    ).map((route) => (
                      <div
                        key={route}
                        className="flex items-center gap-2 text-sm font-medium text-slate-800"
                      >
                        <Route className="h-4 w-4 shrink-0 text-amber-600" />
                        {route}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-800">
                    Recommended vehicles for{" "}
                    {EXTERNAL_REQUIREMENTS.filter((line) =>
                      selectedRequirementIds.includes(line.movementId),
                    ).reduce((sum, line) => sum + line.pax, 0)}{" "}
                    pax
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {batchVehicleRequirements.map((item, index) => (
                      <Badge
                        key={`${item.type}-${index}`}
                        variant="outline"
                        className="border-amber-300 bg-white px-3 py-1 text-amber-900"
                      >
                        {item.quantity} × {item.type}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-slate-600">
                    Review the recommendation below. Vehicle types, quantities,
                    and selected tours can all be changed before creating the
                    batch.
                  </div>
                </div>
              </div>
            )}
            <div className="grid gap-3 rounded-xl bg-indigo-50 p-4 sm:grid-cols-3">
              <div>
                <span className="block text-xs text-slate-500">
                  Selected codes
                </span>
                <b className="text-xl">{selectedRequirementIds.length}</b>
              </div>
              <div>
                <span className="block text-xs text-slate-500">
                  Total passenger movements
                </span>
                <b className="text-xl">
                  {EXTERNAL_REQUIREMENTS.filter((line) =>
                    selectedRequirementIds.includes(line.movementId),
                  ).reduce((sum, line) => sum + line.pax, 0)}
                </b>
              </div>
              <div>
                <span className="block text-xs text-slate-500">
                  Unique routes
                </span>
                <b className="text-xl">
                  {
                    new Set(
                      EXTERNAL_REQUIREMENTS.filter((line) =>
                        selectedRequirementIds.includes(line.movementId),
                      ).map((line) => `${line.pickup}-${line.dropoff}`),
                    ).size
                  }
                </b>
              </div>
            </div>
            <SelectFieldset label="Send to vendor" required>
              <Select value={batchVendorId} onValueChange={setBatchVendorId}>
                <SelectTriggerUI className="h-9 text-sm">
                  <SelectValue placeholder="Select external vendor" />
                </SelectTriggerUI>

                <SelectContent>
                    {DEMO_VENDOR_REQUESTS.filter((vendor) => vendor.isActive).map(
                        (vendor) => (
                        <SelectItem key={vendor.publicId} value={vendor.publicId}>
                            {vendor.name} — {vendor.code}
                        </SelectItem>
                        ),
                    )}
                </SelectContent>
              </Select>
            </SelectFieldset>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Vehicle requirements <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Add each required vehicle type and quantity, for example one
                    bus and one mini coaster.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() =>
                    setBatchVehicleRequirements((current) => [
                      ...current,
                      { type: "Mini Bus", quantity: 1 },
                    ])
                  }
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Type
                </Button>
              </div>
              <div className="space-y-2">
                {batchVehicleRequirements.map((requirement, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-[1fr_130px_40px] gap-2"
                  >
                    <Select
                      value={requirement.type}
                      onValueChange={(type) =>
                        setBatchVehicleRequirements((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, type } : item,
                          ),
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Coach">Coach</SelectItem>
                        <SelectItem value="Bus">Bus</SelectItem>
                        <SelectItem value="Mini Bus">
                          Mini Coaster / Mini Bus
                        </SelectItem>
                        <SelectItem value="Van">Van</SelectItem>
                        <SelectItem value="Sedan">Sedan</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="1"
                      value={requirement.quantity}
                      onChange={(event) =>
                        setBatchVehicleRequirements((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index
                              ? {
                                  ...item,
                                  quantity: Math.max(
                                    1,
                                    Number(event.target.value),
                                  ),
                                }
                              : item,
                          ),
                        )
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={batchVehicleRequirements.length === 1}
                      onClick={() =>
                        setBatchVehicleRequirements((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-800">
                Multi-route vehicle requirement plan
              </h3>
              <p className="mb-3 text-xs text-slate-500">
                Like the transport schedule, each required vehicle type shows
                all routes it may need to cover. The vendor confirms
                availability; final vehicle allocation remains with Transport
                Operations.
              </p>
              <div className="space-y-3">
                {batchVehicleRequirements.map((vehicleNeed, vehicleIndex) => (
                  <div
                    key={`${vehicleNeed.type}-${vehicleIndex}`}
                    className="overflow-hidden rounded-xl border border-sky-200"
                  >
                    <div className="flex items-center justify-between bg-sky-50 px-4 py-3">
                      <div className="flex items-center gap-2 font-semibold text-slate-900">
                        <BusFront className="h-4 w-4 text-sky-600" />
                        {vehicleNeed.quantity} × {vehicleNeed.type}
                      </div>
                      <Badge variant="outline" className="bg-white">
                        {selectedRequirementIds.length} transport codes
                      </Badge>
                    </div>
                    <div className="overflow-x-auto p-4">
                      <div className="flex min-w-max items-stretch gap-2">
                        {EXTERNAL_REQUIREMENTS.filter((line) =>
                          selectedRequirementIds.includes(line.movementId),
                        ).map((line, routeIndex) => (
                          <div
                            key={line.movementId}
                            className="flex items-center"
                          >
                            <div className="w-56 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                              <div className="flex items-center justify-between">
                                <b className="text-xs text-sky-700">
                                  {line.movementId}
                                </b>
                                <span className="text-xs font-semibold">
                                  {line.pax} pax
                                </span>
                              </div>
                              <div className="mt-2 text-xs">
                                <div className="font-medium">{line.pickup}</div>
                                <div className="text-slate-500">
                                  {line.pickupTime}
                                </div>
                                <div className="my-1 border-l-2 border-dashed border-sky-300 pl-2 text-sky-600">
                                  to
                                </div>
                                <div className="font-medium">
                                  {line.dropoff}
                                </div>
                                <div className="text-slate-500">
                                  {line.dropoffTime}
                                </div>
                              </div>
                            </div>
                            {routeIndex < selectedRequirementIds.length - 1 && (
                              <div className="mx-1 h-px w-6 bg-sky-300" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-800">
                Additional instructions / requirement text
              </label>
              <textarea
                value={batchNotes}
                onChange={(event) => setBatchNotes(event.target.value)}
                rows={3}
                className="w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                placeholder="Add reporting instructions, luggage requirement, driver instructions, contact details, or any other message for the vendor…"
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Select tours and requirements
                  </h3>
                  <p className="text-xs text-slate-500">
                    Select or deselect any recommended tour movement before
                    creating the batch.
                  </p>
                </div>
                <span className="text-xs font-semibold text-sky-700">
                  {selectedRequirementIds.length} of {batchCandidateIds.length}{" "}
                  selected
                </span>
              </div>
              <div className="max-h-52 overflow-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          aria-label="Select all recommended tours"
                          checked={
                            batchCandidateIds.length > 0 &&
                            batchCandidateIds.every((id) =>
                              selectedRequirementIds.includes(id),
                            )
                          }
                          onChange={(event) =>
                            setSelectedRequirementIds(
                              event.target.checked ? batchCandidateIds : [],
                            )
                          }
                        />
                      </TableHead>
                      <TableHead>Code / Tour</TableHead>
                      <TableHead>Pax</TableHead>
                      <TableHead>Pickup</TableHead>
                      <TableHead>Drop-off</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {EXTERNAL_REQUIREMENTS.filter((line) =>
                      batchCandidateIds.includes(line.movementId),
                    ).map((line) => (
                      <TableRow
                        key={line.movementId}
                        className={
                          selectedRequirementIds.includes(line.movementId)
                            ? "bg-sky-50/50"
                            : "opacity-60"
                        }
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            aria-label={`Select ${line.tour}`}
                            checked={selectedRequirementIds.includes(
                              line.movementId,
                            )}
                            onChange={() =>
                              setSelectedRequirementIds((current) =>
                                current.includes(line.movementId)
                                  ? current.filter(
                                      (id) => id !== line.movementId,
                                    )
                                  : [...current, line.movementId],
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <b>{line.movementId}</b>
                          <div className="text-xs text-slate-500">
                            {line.tour}
                          </div>
                        </TableCell>
                        <TableCell>{line.pax}</TableCell>
                        <TableCell>
                          {line.pickup}
                          <div className="text-xs text-slate-500">
                            {line.pickupDate} · {line.pickupTime}
                          </div>
                        </TableCell>
                        <TableCell>
                          {line.dropoff}
                          <div className="text-xs text-slate-500">
                            {line.dropoffDate} · {line.dropoffTime}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                onClick={() => setCreateBatchOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="gap-2 bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={createRequirementBatch}
              >
                <Send className="h-4 w-4" />
                Create &amp; Send Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selectedBatch}
        onOpenChange={(open) => !open && setSelectedBatch(null)}
      >
        <DialogContent className="max-h-[92vh] w-[96vw] max-w-5xl overflow-y-auto bg-white">
          <DialogHeader className="border-b pb-4">
            <DialogTitle>Vendor Requirement Details</DialogTitle>
          </DialogHeader>
          {selectedBatch && (
            <div className="space-y-5">
              <div className="grid gap-3 rounded-xl bg-sky-50 p-4 sm:grid-cols-4">
                <div>
                  <span className="block text-xs text-slate-500">Batch</span>
                  <b className="text-sky-700">{selectedBatch.batchCode}</b>
                </div>
                <div>
                  <span className="block text-xs text-slate-500">Vendor</span>
                  <b>{selectedBatch.vendorName}</b>
                </div>
                <div>
                  <span className="block text-xs text-slate-500">
                    Total passengers
                  </span>
                  <b>
                    {selectedBatch.lines.reduce(
                      (sum, line) => sum + line.pax,
                      0,
                    )}
                  </b>
                </div>
                <div>
                  <span className="block text-xs text-slate-500">Status</span>
                  <b>{selectedBatch.status}</b>
                </div>
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold">
                  Required vehicles
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedBatch.vehicleRequirements.map((item) => (
                    <Badge
                      key={item.type}
                      variant="outline"
                      className="px-3 py-1.5"
                    >
                      {item.quantity} × {item.type}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold">
                  Included transport codes
                </h3>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Code / Tour</TableHead>
                        <TableHead>Movement</TableHead>
                        <TableHead>Pax</TableHead>
                        <TableHead>Pickup</TableHead>
                        <TableHead>Drop-off</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedBatch.lines.map((line) => (
                        <TableRow key={line.movementId}>
                          <TableCell>
                            <b>{line.movementId}</b>
                            <div className="text-xs text-slate-500">
                              {line.tour}
                              <br />
                              {line.operator}
                            </div>
                          </TableCell>
                          <TableCell>{line.movement}</TableCell>
                          <TableCell>{line.pax}</TableCell>
                          <TableCell>
                            {line.pickup}
                            <div className="text-xs text-slate-500">
                              {line.pickupDate} · {line.pickupTime}
                            </div>
                          </TableCell>
                          <TableCell>
                            {line.dropoff}
                            <div className="text-xs text-slate-500">
                              {line.dropoffDate} · {line.dropoffTime}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              {selectedBatch.notes && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                  <div className="text-xs font-semibold uppercase text-sky-700">
                    Additional instructions
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                    {selectedBatch.notes}
                  </div>
                </div>
              )}
              {selectedBatch.vendorResponse && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs font-semibold uppercase text-emerald-700">
                    Vendor response
                  </div>
                  <div className="mt-1 text-sm">
                    {selectedBatch.vendorResponse}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setSelectedBatch(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!responseBatch}
        onOpenChange={(open) => !open && setResponseBatch(null)}
      >
        <DialogContent className="w-[94vw] max-w-lg bg-white">
          <DialogHeader className="border-b pb-4">
            <DialogTitle>Record Vendor Availability</DialogTitle>
          </DialogHeader>
          {responseBatch && (
            <div className="space-y-4">
              <div className="rounded-lg bg-sky-50 p-3">
                <b>{responseBatch.batchCode}</b>
                <div className="text-sm text-slate-600">
                  {responseBatch.vendorName} ·{" "}
                  {responseBatch.vehicleRequirements
                    .map((item) => `${item.quantity} × ${item.type}`)
                    .join(", ")}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Availability response
                </label>
                <Select
                  value={batchResponse.status}
                  onValueChange={(status) =>
                    setBatchResponse((current) => ({ ...current, status }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Partially Available">
                      Partially Available
                    </SelectItem>
                    <SelectItem value="Unavailable">Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Vendor remarks</label>
                <Input
                  value={batchResponse.note}
                  onChange={(event) =>
                    setBatchResponse((current) => ({
                      ...current,
                      note: event.target.value,
                    }))
                  }
                  placeholder="e.g. Two coaches available for the requested timing"
                />
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                The vendor only confirms availability here. Vehicle registration
                and final assignment remain with Transport Operations.
              </div>
              <div className="flex justify-end gap-2 border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => setResponseBatch(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-[#5B9BD5] text-white hover:bg-[#4A8AC5]"
                  onClick={saveBatchResponse}
                >
                  Save Response
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createPoOpen} onOpenChange={setCreatePoOpen}>
        <DialogContent className="!w-[96vw] !max-w-[1100px] max-h-[92vh] overflow-y-auto bg-white">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2 text-xl text-slate-900">
              <ShoppingCart className="h-5 w-5 text-[#5B9BD5]" />
              Create Purchase Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-1">
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                New transport purchase order
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Select the vendor and enter the required service and commercial
                details.
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2 lg:col-span-2">
                <label className="text-sm font-medium text-slate-700">
                  Vendor / Supplier <span className="text-red-500">*</span>
                </label>
                <Select
                  value={poForm.vendor}
                  onValueChange={(vendor) =>
                    setPoForm((current) => ({ ...current, vendor }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEMO_VENDOR_REQUESTS.filter(
                      (vendor) => vendor.isActive,
                    ).map((vendor) => (
                      <SelectItem key={vendor.publicId} value={vendor.name}>
                        {vendor.name} — {vendor.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  PO date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={poForm.poDate}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      poDate: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2 lg:col-span-2">
                <label className="text-sm font-medium text-slate-700">
                  Vendor request batch reference
                </label>
                <Select
                  value={poForm.batchCode || "none"}
                  onValueChange={(batchCode) => {
                    const batch = vendorBatches.find(
                      (item) => item.batchCode === batchCode,
                    );
                    setPoForm((current) => ({
                      ...current,
                      batchCode: batchCode === "none" ? "" : batchCode,
                      vendor: batch?.vendorName || current.vendor,
                      service: batch
                        ? `Transport services for ${batch.lines.map((line) => line.movementId).join(", ")}`
                        : current.service,
                      deliveryLocation:
                        batch?.lines[0]?.pickup || current.deliveryLocation,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No batch reference</SelectItem>
                    {vendorBatches.map((batch) => (
                      <SelectItem key={batch.id} value={batch.batchCode}>
                        {batch.batchCode} — {batch.vendorName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Payment terms <span className="text-red-500">*</span>
                </label>
                <Select
                  value={poForm.paymentTerms}
                  onValueChange={(paymentTerms) =>
                    setPoForm((current) => ({ ...current, paymentTerms }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Advance Payment">
                      Advance Payment
                    </SelectItem>
                    <SelectItem value="Due on Delivery">
                      Due on Delivery
                    </SelectItem>
                    <SelectItem value="Net 15 Days">Net 15 Days</SelectItem>
                    <SelectItem value="Net 30 Days">Net 30 Days</SelectItem>
                    <SelectItem value="Net 45 Days">Net 45 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="text-sm font-medium text-slate-700">
                  Service / line-item description{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  value={poForm.service}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      service: event.target.value,
                    }))
                  }
                  placeholder="e.g. Two coaches for airport pickup and return transfer"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Quantity <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  min="1"
                  value={poForm.quantity}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      quantity: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Unit <span className="text-red-500">*</span>
                </label>
                <Select
                  value={poForm.unit}
                  onValueChange={(unit) =>
                    setPoForm((current) => ({ ...current, unit }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Vehicle">Vehicle</SelectItem>
                    <SelectItem value="Trip">Trip</SelectItem>
                    <SelectItem value="Day">Day</SelectItem>
                    <SelectItem value="Service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Unit rate (USD) <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={poForm.unitRate}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      unitRate: event.target.value,
                    }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Tax / additional charges (USD)
                </label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={poForm.tax}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      tax: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Delivery / service date{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={poForm.delivery}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      delivery: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Delivery / reporting location{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  value={poForm.deliveryLocation}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      deliveryLocation: event.target.value,
                    }))
                  }
                  placeholder="e.g. Najaf International Airport"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="text-sm font-medium text-slate-700">
                  Terms, instructions and remarks
                </label>
                <textarea
                  value={poForm.notes}
                  onChange={(event) =>
                    setPoForm((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full resize-y rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  placeholder="Reporting time, driver/contact requirement, vehicle condition, cancellation terms, invoice instructions…"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-900 px-4 py-3 text-white">
              <span className="text-sm">Calculated PO Total</span>
              <b className="text-xl">
                USD{" "}
                {(
                  (Number(poForm.quantity) || 0) *
                    (Number(poForm.unitRate) || 0) +
                  (Number(poForm.tax) || 0)
                ).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </b>
            </div>
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => setCreatePoOpen(false)}>
                Cancel
              </Button>
              <Button
                className="gap-2 bg-[#5B9BD5] text-white hover:bg-[#4A8AC5]"
                onClick={createPurchaseOrder}
              >
                <Plus className="h-4 w-4" />
                Create Purchase Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!assignmentVendor}
        onOpenChange={(open) => !open && setAssignmentVendor(null)}
      >
        <DialogContent className="w-[94vw] max-w-2xl bg-white">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2 text-xl text-slate-900">
              <Route className="h-5 w-5 text-[#5B9BD5]" />
              Assign Tour &amp; Vehicle
            </DialogTitle>
          </DialogHeader>
          {assignmentVendor && (
            <div className="space-y-5 pt-1">
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                  Vendor request
                </div>
                <div className="mt-1 font-semibold text-slate-900">
                  {assignmentVendor.name}
                </div>
                <div className="text-sm text-slate-500">
                  {assignmentVendor.code} ·{" "}
                  {assignmentVendor.venues
                    ?.map((venue) => venue.name)
                    .join(", ")}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Tour <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={assignmentForm.tourId}
                    onValueChange={(tourId) =>
                      setAssignmentForm((current) => ({ ...current, tourId }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tour" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEMO_TOURS.map((tour) => (
                        <SelectItem key={tour.id} value={tour.id}>
                          {tour.id} — {tour.name} ({tour.passengers} pax)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {assignmentForm.tourId && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Route className="h-3.5 w-3.5" />
                      {
                        DEMO_TOURS.find(
                          (tour) => tour.id === assignmentForm.tourId,
                        )?.route
                      }
                    </div>
                  )}
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Vehicle <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={assignmentForm.vehicleId}
                    onValueChange={(vehicleId) =>
                      setAssignmentForm((current) => ({
                        ...current,
                        vehicleId,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEMO_VEHICLES.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.id} — {vehicle.name} · {vehicle.type} (
                          {vehicle.capacity} seats)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {assignmentForm.vehicleId && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <BusFront className="h-3.5 w-3.5" />
                      Assigned vehicle capacity:{" "}
                      {
                        DEMO_VEHICLES.find(
                          (vehicle) => vehicle.id === assignmentForm.vehicleId,
                        )?.capacity
                      }{" "}
                      seats
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Movement date <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    value={assignmentForm.date}
                    onChange={(event) =>
                      setAssignmentForm((current) => ({
                        ...current,
                        date: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Reporting time <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="time"
                    value={assignmentForm.time}
                    onChange={(event) =>
                      setAssignmentForm((current) => ({
                        ...current,
                        time: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => setAssignmentVendor(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="gap-2 bg-[#5B9BD5] text-white hover:bg-[#4A8AC5]"
                  onClick={saveAssignment}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Confirm Assignment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selectedPo}
        onOpenChange={(open) => !open && setSelectedPo(null)}
      >
        <DialogContent className="!w-[96vw] !max-w-[1400px] max-h-[92vh] overflow-x-hidden overflow-y-auto bg-white sm:!w-[94vw] lg:!w-[90vw]">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-xl text-slate-900">
              Purchase Order
            </DialogTitle>
          </DialogHeader>
          {selectedPo && (
            <div
              id="vendor-purchase-order"
              className="space-y-6 p-1 text-slate-700"
            >
              <div className="flex flex-col justify-between gap-4 border-b border-slate-200 pb-5 sm:flex-row">
                <div>
                  <div className="text-2xl font-bold tracking-wide text-[#0B5394]">
                    FHIMS
                  </div>
                  <div className="font-medium">Faiz e Husaini – Iraq</div>
                  <div className="text-sm text-slate-500">
                    Information Management System
                  </div>
                </div>
                <div className="sm:text-right">
                  <div className="text-2xl font-bold text-slate-800">
                    PURCHASE ORDER
                  </div>
                  <div className="mt-1 font-semibold text-[#0B5394]">
                    {selectedPo.po}
                  </div>
                  <div className="text-sm text-slate-500">
                    Created: {selectedPo.date}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                    Supplier
                  </div>
                  <div className="font-semibold text-slate-900">
                    {selectedPo.vendor}
                  </div>
                  <div className="mt-1 text-sm">Transport Services Vendor</div>
                  <div className="text-sm">Iraq</div>
                </div>
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                    Bill To
                  </div>
                  <div className="font-semibold text-slate-900">
                    Faiz e Husaini – Iraq
                  </div>
                  <div className="mt-1 text-sm">
                    Transport Operations Department
                  </div>
                  <div className="text-sm">FHIMS Administration</div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 rounded-lg bg-sky-50 p-4 text-sm sm:grid-cols-5">
                <div>
                  <span className="block text-xs text-slate-500">
                    Batch Reference
                  </span>
                  <span className="font-semibold">{selectedPo.batchCode}</span>
                </div>
                <div>
                  <span className="block text-xs text-slate-500">
                    Delivery Date
                  </span>
                  <span className="font-semibold">{selectedPo.delivery}</span>
                </div>
                <div>
                  <span className="block text-xs text-slate-500">
                    Delivery Location
                  </span>
                  <span className="font-semibold">
                    {selectedPo.deliveryLocation}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-slate-500">
                    Payment Terms
                  </span>
                  <span className="font-semibold">
                    {selectedPo.paymentTerms}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-slate-500">Currency</span>
                  <span className="font-semibold">USD</span>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead>#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Unit Rate</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>1</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">
                          {selectedPo.service}
                        </div>
                        <div className="text-xs text-slate-500">
                          Transport service as per vendor requirement batch and
                          route schedule
                        </div>
                      </TableCell>
                      <TableCell>{selectedPo.quantity}</TableCell>
                      <TableCell>{selectedPo.unit}</TableCell>
                      <TableCell>{selectedPo.unitRate}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {selectedPo.amount}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="ml-auto w-full space-y-2 sm:w-80">
                <div className="flex justify-between border-b py-2">
                  <span>Line Total</span>
                  <span>{selectedPo.amount}</span>
                </div>
                <div className="flex justify-between border-b py-2">
                  <span>Tax / Charges</span>
                  <span>{selectedPo.tax}</span>
                </div>
                <div className="flex justify-between bg-slate-900 px-4 py-3 font-bold text-white">
                  <span>Total</span>
                  <span>{selectedPo.amount}</span>
                </div>
              </div>
              {selectedPo.notes && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                    Terms &amp; Instructions
                  </div>
                  <div className="whitespace-pre-wrap text-sm">
                    {selectedPo.notes}
                  </div>
                </div>
              )}

              <div className="grid gap-8 pt-5 sm:grid-cols-2">
                <div>
                  <div className="border-t border-slate-400 pt-2 text-center text-sm">
                    Prepared By
                    <br />
                    <span className="text-xs text-slate-500">
                      Transport Operations
                    </span>
                  </div>
                </div>
                <div>
                  <div className="border-t border-slate-400 pt-2 text-center text-sm">
                    Authorized Signatory
                    <br />
                    <span className="text-xs text-slate-500">
                      FHIMS Administration
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button variant="outline" onClick={() => setSelectedPo(null)}>
                  Close
                </Button>
                <Button
                  className="gap-2 bg-[#5B9BD5] text-white hover:bg-[#4A8AC5]"
                  onClick={() => window.print()}
                >
                  <Printer className="h-4 w-4" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
